import type { MarkerOptions } from "./types";
import type { Region } from "./coordinates";
import type { Drawing, PrivateNode } from "./settings";
import { Game, games, getAppDomain } from "./games";
import { layerDicts, parseCompactJson, sortByDictOrder } from "./compact-dict";

export type IconName =
  | "House"
  | "Map"
  | "Server"
  | "BookOpen"
  | "ScrollText"
  | "ArrowUp"
  | "Bug"
  | "NotepadText"
  | "Axe"
  | "Gift"
  | "MapPin"
  | "Trophy"
  | "SquareCheckBig"
  | "MessageSquareWarning"
  | "Grid"
  | "Megaphone"
  | "MonitorSmartphone"
  | "Heart"
  | "Handshake"
  | "Newspaper"
  | "MessageSquare"
  | "HelpCircle"
  | "FileText"
  | "ShieldCheck"
  | "Fish"
  | "Bird"
  | "ChefHat"
  | "Flower"
  | "PawPrint"
  | "Users"
  | "CloudSun"
  | "Shield"
  | "Sparkles"
  | "ChartLine"
  | "Vote"
  | "Activity"
  | "Code"
  | "Hammer";

export type AppConfig = {
  name: string;
  domain: string;
  title: string;
  supportedLocales: string[];
  keywords: string[];
  appUrl: string | null;
  withoutLiveMode?: boolean;
  /**
   * A notice the game's license holder requires, shown in the footer of every game page
   * (Baldur's Gate: Wizards of the Coast's Fan Content Policy text). Plain text, as given.
   */
  legalNotice?: string;
  /**
   * Gate the whole tenant behind an "In Development" placeholder in production.
   * The real site still renders on the local dev server (NODE_ENV !== "production")
   * so the game can be worked on before it's ready to ship publicly.
   */
  inDevelopment?: boolean;
  internalLinks?: {
    title: string;
    description?: string;
    href: string;
    linkText?: string;
    bgImage?: string;
    iconName: IconName;
    /** Elite-only (preview) link — hidden from nav/home/sitemap for everyone
     *  else. Used to soft-launch a WIP page of a live game. */
    previewOnly?: boolean;
  }[];
  promoLinks?: {
    title: string;
    href: string;
  }[];
  externalLinks?: { href: string; title: string }[];
  markerOptions?: MarkerOptions;
  game?: Game;
  /** Featured filter IDs to highlight on the home page. If not set, first filters are shown. */
  topFilters?: string[];
  /**
   * Filter groups or categories whose markers are spots players reported a catch or sighting
   * at (Palia fish and bugs), not fixed spawns: their guide shows the map without the
   * per-spot progress tracker, since there is nothing to tick off.
   */
  sightingFilters?: string[];
  /**
   * Filter groups, categories or type ids whose spots are gone for the player once
   * gathered or opened (chests, unlockables): only their guides get the per-spot progress
   * tracker. Unset = every guide has one. Respawning nodes have nothing to tick off.
   */
  trackerFilters?: string[];
  /**
   * Live market prices in the crafting calculator. `"aodp"` = the Albion
   * Online Data Project's crowd-sourced price API (Albion only).
   */
  craftingMarket?: "aodp";
  /**
   * Database-mode settings. When set, the app renders as a DB site
   * (custom landing page with entity counts, header search instead of
   * settings) rather than a map site. Used by homm-olden-era and
   * other future game-database deployments.
   */
  db?: DbAppConfig;
};

export type DbAppConfig = {
  /** Tagline rendered under the H1 (e.g. "Game Database"). */
  heroSubtitle: string;
  /** Placeholder text for the hero search button. */
  searchPlaceholder: string;
  /** Sections rendered as cards on the landing page. */
  homeSections: Array<{
    href: string;
    /** Dict key for the section title (resolved at render time). */
    titleKey?: string;
    /** Fallback label if the dict key doesn't resolve. */
    titleFallback?: string;
    /** Database entry type used to compute the count badge. */
    type: string;
    /** Extra entry types whose item counts should be added to this section. */
    extraTypes?: string[];
    /**
     * Match every database category whose `type` starts with this prefix.
     * Used by games that ship one category per sub-group (e.g. BPSR's
     * `dictionary_historical_events`, `dictionary_concepts`, ...) instead
     * of a single category per section. When set, the section's URL
     * receives every matching entry; `type`/`extraTypes` are still honoured
     * for exact matches alongside.
     */
    typePrefix?: string;
    /**
     * When a list group's id is itself a DB entry (e.g. abilities grouped per
     * Descendant), also list that entry's refs under this prop key inside the
     * group, linking to their own section (e.g. `transcendentModules`).
     */
    groupRefs?: string;
    /** Glyph rendered to the left of the card title. */
    icon: string;
    /** Optional description. If absent, falls back to the matching internalLink description. */
    description?: string;
  }>;
  /**
   * Extra per-entry pages outside /db, one per entry of a database type — e.g.
   * Palworld's `/breeding/<palId>` for every `paldeck` entry. Feeds the sitemap
   * and (with `labelKey`) a link on the /db entry page; the route lives in the app.
   */
  entryPages?: Array<{
    type: string;
    path: string;
    /** Dict key (`{{name}}` = entry name) for a link from the /db entry page. */
    labelKey?: string;
  }>;
  /**
   * Codex sections that get a collection checklist (`/checklist/<section>`,
   * hub at `/checklist`): every entry with a "have it" tick, progress, group /
   * missing-only filters and links to its codex entry and map spawns. Only
   * collectible sections (bestiaries, fish, outfits, lore…), never raw items.
   * `section` is a `homeSections` slug (`/db/<section>`), or one database
   * type that a home section aggregates via `extraTypes` (Gothic's runes under
   * "Items"): its entries link to the aggregating section's codex pages.
   */
  checklists?: Array<{
    section: string;
    /**
     * A slice of another section: `section` is then only the URL slug, the
     * entries are the `groups` (groupIds) of the `/db/<from>` home section and
     * link to its codex pages (Avowed's unique weapons inside "Items").
     */
    from?: string;
    groups?: string[];
    /** Dict key of the plural section label (a database type has none). */
    titleKey?: string;
    /** Card glyph (defaults to the home section's icon). */
    icon?: string;
    /** Show each entry's `<id>_desc` under its name (achievements, titles). */
    descriptions?: boolean;
    /**
     * Codex ids of rewards (`props.rewards[].id`) to total up: "earned / all"
     * over the ticked entries plus each entry's amount (e.g. the currency
     * every achievement pays out).
     */
    rewardTotals?: string[];
  }>;
  /** Full-width links rendered below the section grid (e.g. Game Mechanics). */
  homeExtraLinks?: Array<{
    href: string;
    title: string;
    description: string;
    icon: string;
  }>;
  /** Per-entry-type display labels shown in the header search dropdown. */
  typeLabels?: Record<string, string>;
  /** Per-entry-type Tailwind classes (bg + text colour) for the search dropdown badges. */
  typeColors?: Record<string, string>;
  /**
   * When true, every `homeSections` entry also becomes a header-nav item
   * (de-duplicated against `internalLinks` by href, overflowing into the
   * "More" menu). Lets the nav stay data-driven from the section list instead
   * of hand-curating each section in `internalLinks`. Off by default so other
   * tenants keep their curated navs.
   */
  sectionsInNav?: boolean;
  /**
   * DB sections listed as headed groups (in data order) instead of group filter chips, like a
   * hand-built page (Olden Era heroes) - e.g. Baldur's Gate EE characters: companions, merchants,
   * characters. The name filter stays.
   */
  groupHeaderSections?: string[];
  /**
   * Hide the standalone interactive-map nav link + home map cards even when the
   * game HAS tiles. For games where the map is better browsed as a DB "Maps"
   * section (e.g. Soul's Remnant's tiny side-scroller levels) rather than the
   * leaflet interactive map. The /maps routes still resolve; they're just not
   * surfaced in the chrome.
   */
  hideInteractiveMap?: boolean;
  /**
   * Entry pages answer the "<Name> location / how to get / drops" search
   * intent: title "<Name>: Location, How to Get and Uses | <Game>" (only the
   * facets the entry's props back), an H1 line with the same facets, and a
   * fact summary from the props at the top + leading the meta description.
   * Pilot flag (inbox #431): title changes on thousands of pages are an SEO
   * risk, so it rolls out per game after Search Console shows the effect.
   */
  entityIntent?: boolean;
  /**
   * Number of UI translations to display on the landing page. Defaults to
   * `appConfig.supportedLocales.length` when omitted.
   */
  languageCount?: number;
};

export type OverwolfAppConfig = {
  name: string;
  domain: string;
  title: string;
  gameClassId: number;
  appUrl: string;
  withoutLiveMode?: boolean;
  appId: string;
  discordApplicationId: string;
  markerOptions: MarkerOptions;
  /** Languages offered in the settings dialog (same list as the web config).
   *  Omitted = English only, no language picker. */
  supportedLocales?: string[];
};

export type THGLAppConfig = {
  name: string;
  domain: string;
  title: string;
  withoutOverlayMode?: boolean;
  markerOptions: MarkerOptions;
  defaultHotkeys: Record<string, string>;
};

/**
 * Single source of truth for game config (see CLAUDE.md "Single source of
 * truth for game config").
 *
 * `games` (the `Game[]` registry in games.ts) is canonical. The per-surface
 * configs — web `AppConfig` (configs/*.ts) and `OverwolfAppConfig`
 * (*-overwolf/src/config.ts) — only carry surface-specific fields plus a
 * `name` that links back to `Game.id`. Shared fields (`title`, `domain`,
 * `markerOptions`) are NOT re-declared there; they are derived from the linked
 * `Game` by the resolvers below. The strict output types (`AppConfig`,
 * `OverwolfAppConfig`) keep those fields required so component consumers are
 * unchanged; the `*Input` types make the derivable fields optional so a config
 * file can omit them (or override when no `Game` exists, e.g. thgl-web).
 */

/** A game's marker render options, regardless of where they live on `Game`. */
export function getGameMarkerOptions(game: Game): MarkerOptions | undefined {
  return game.markerOptions ?? game.companion?.markerOptions;
}

/** Web config as authored: `title`/`domain` optional (derived from `Game`). */
export type AppConfigInput = Omit<AppConfig, "title" | "domain"> &
  Partial<Pick<AppConfig, "title" | "domain">>;

/** Overwolf config as authored: derivable fields optional. */
export type OverwolfAppConfigInput = Omit<
  OverwolfAppConfig,
  "title" | "domain" | "markerOptions"
> &
  Partial<Pick<OverwolfAppConfig, "title" | "domain" | "markerOptions">>;

/**
 * Fill a web `AppConfig`'s shared fields from its linked `Game`. Authored
 * values win (override); when absent they fall back to the registry. Configs
 * with no matching `Game` (thgl-web, thgl-app, drakantos) must supply their
 * own `title`/`domain`.
 */
export function resolveAppConfig(cfg: AppConfigInput): AppConfig {
  const game = games.find((g) => g.id === cfg.name);
  return {
    ...cfg,
    title: cfg.title ?? game?.title ?? cfg.name,
    domain: cfg.domain ?? (game ? getAppDomain(game) : cfg.name),
    markerOptions: cfg.markerOptions ?? (game && getGameMarkerOptions(game)),
    // Embed the linked Game so consumers can read game-level data (e.g.
    // additionalTooltip in guide-page) without a second registry lookup.
    game: cfg.game ?? game,
  };
}

/** True when `appUrl` points at the Companion App: the shared
 *  www.th.gl/companion-app page or a game's www.th.gl/apps/<id> page. */
export function isCompanionAppUrl(appUrl: string | null | undefined): boolean {
  return !!appUrl && /th\.gl\/(companion-app|apps\/)/.test(appUrl);
}

/**
 * Fill an `OverwolfAppConfig`'s shared fields from its linked `Game`. The
 * store identifiers (`appId`/`appUrl`) stay in the overwolf config and are
 * never hoisted to the public registry.
 */
export function resolveOverwolfConfig(
  cfg: OverwolfAppConfigInput,
): OverwolfAppConfig {
  const game = games.find((g) => g.id === cfg.name);
  const markerOptions =
    cfg.markerOptions ?? (game && getGameMarkerOptions(game));
  if (!markerOptions) {
    throw new Error(
      `resolveOverwolfConfig: no markerOptions for "${cfg.name}" (set them on the Game or in the overwolf config)`,
    );
  }
  return {
    ...cfg,
    title: cfg.title ?? game?.title ?? cfg.name,
    domain: cfg.domain ?? (game ? getAppDomain(game) : cfg.name),
    markerOptions,
  };
}

export type Version = {
  id: string;
  createdAt: number;
  data: {
    filters: FiltersConfig;
    regions: Region[];
    tiles: TilesConfig;
    globalFilters: GlobalFiltersConfig;
    typesIdMap: Record<string, string>;
    drawings: DrawingsConfig;
  };
  more: {
    nodes: Record<string, string>;
    icons: string;
    /** Changes whenever the dicts or database output change (see withContentHash). */
    contentHash?: string;
  };
  /** Spawn counts for UI display */
  counts?: {
    /** Total spawns across all maps */
    total: number;
    /** Spawn count per filter type ID */
    byType: Record<string, number>;
    /** Spawn count per map name */
    byMap: Record<string, number>;
  };
};

/**
 * All env reads in this file must stay literal `process.env.NEXT_PUBLIC_*`
 * member expressions so Next.js can inline them into client bundles. Vite
 * apps (Overwolf) don't shim `process` in the browser — every
 * vite.config.ts must register thglEnvDefine() from @repo/lib/vite-define
 * or this module throws "process is not defined" at startup.
 */
// TH_GL_URL is defined in the leaf env.ts (so games.ts can use it for logo URLs
// without a config<->games circular import); re-exported here for back-compat.
export { TH_GL_URL } from "./env";
export const API_FORGE_URL =
  process.env.NEXT_PUBLIC_API_FORGE_URL ?? "https://api-forge.th.gl";

/**
 * Dev-only forge proxy (games-web). When NEXT_PUBLIC_FORGE_DEV_PROXY is on
 * (set by apps/games-web/next.config.js in `next dev`), DATA_FORGE_URL and
 * DATA_FORGE_CDN_URL become same-origin paths. games-web's proxy.ts
 * forwards them per request: tenants whose first host label ends in "-dev"
 * (e.g. palia-dev.localhost:3100) hit the local data-forge dev server,
 * every other host the prod endpoints. Because the rendered markup only
 * ever contains the host-independent relative URL, SSR and client output
 * match on both hosts (no hydration mismatch) and forge requests are
 * same-origin (no CORS).
 *
 * Server-side fetch() can't take relative URLs — resolveForgeUrl() maps
 * them back to the absolute target using the per-request Host.
 */
export const FORGE_DEV_PROXY = process.env.NEXT_PUBLIC_FORGE_DEV_PROXY === "1";
export const FORGE_API_PROXY_PATH = "/__forge-api";
export const FORGE_CDN_PROXY_PATH = "/__forge-cdn";
/** The local data-forge dev server serves both API and CDN content. */
export const FORGE_LOCAL_TARGET = "http://localhost:33033";

const DATA_FORGE_PROD_URL =
  process.env.NEXT_PUBLIC_DATA_FORGE_URL ?? "https://api.th.gl";
const DATA_FORGE_CDN_PROD_URL =
  process.env.NEXT_PUBLIC_DATA_FORGE_CDN_URL ?? "https://cdn.th.gl";

// API endpoints (search)
export const DATA_FORGE_URL = FORGE_DEV_PROXY
  ? FORGE_API_PROXY_PATH
  : DATA_FORGE_PROD_URL;

// Static files (version.json, icons, tiles, config, dicts)
export const DATA_FORGE_CDN_URL = FORGE_DEV_PROXY
  ? FORGE_CDN_PROXY_PATH
  : DATA_FORGE_CDN_PROD_URL;

/**
 * True when the request host opts into the local data-forge: the first
 * label of a *.localhost host ends in "-dev" (palia-dev.localhost:3100).
 */
export function isDevForgeHost(host: string): boolean {
  const hostname = host.split(":")[0].replace(/\.$/, "");
  return (
    hostname.endsWith(".localhost") && hostname.split(".")[0].endsWith("-dev")
  );
}

/**
 * Resolve the origin a forge proxy path should be forwarded to for the
 * given request host, or null if the path is not a forge proxy path.
 * Used by games-web's proxy.ts.
 */
export function getForgeProxyTarget(
  host: string,
  pathname: string,
): string | null {
  const local = isDevForgeHost(host);
  if (pathname.startsWith(`${FORGE_CDN_PROXY_PATH}/`)) {
    return local ? FORGE_LOCAL_TARGET : DATA_FORGE_CDN_PROD_URL;
  }
  if (pathname.startsWith(`${FORGE_API_PROXY_PATH}/`)) {
    return local ? FORGE_LOCAL_TARGET : DATA_FORGE_PROD_URL;
  }
  return null;
}

type RequestHostResolver = () => Promise<string | null>;
/**
 * Stored on globalThis (not module state) because games-web registers the
 * resolver from instrumentation.ts, which may not share module instances
 * with the RSC server graph.
 */
const REQUEST_HOST_RESOLVER_KEY = "__thglRequestHostResolver";

/**
 * Registered once at server start by games-web (instrumentation.ts) with a
 * next/headers-based callback, so this package can read the per-request
 * Host without importing next/headers (which Vite apps can't resolve).
 */
export function setRequestHostResolver(resolver: RequestHostResolver): void {
  (globalThis as Record<string, unknown>)[REQUEST_HOST_RESOLVER_KEY] = resolver;
}

/**
 * Map a proxy-relative forge URL (see FORGE_DEV_PROXY) back to its real
 * absolute target. No-op for absolute URLs and in the browser — there the
 * relative URL is correct, the request carries the Host that proxy.ts
 * switches on. On the server it resolves directly against the current
 * request's host, skipping the proxy hop.
 */
export async function resolveForgeUrl(url: string): Promise<string> {
  if (typeof window !== "undefined" || !url.startsWith("/")) return url;
  const prefix = url.startsWith(`${FORGE_CDN_PROXY_PATH}/`)
    ? FORGE_CDN_PROXY_PATH
    : url.startsWith(`${FORGE_API_PROXY_PATH}/`)
      ? FORGE_API_PROXY_PATH
      : null;
  if (!prefix) return url;
  const resolver = (globalThis as Record<string, unknown>)[
    REQUEST_HOST_RESOLVER_KEY
  ] as RequestHostResolver | undefined;
  // No resolver or no request scope (e.g. build-time render) → prod.
  const host = (await resolver?.().catch(() => null)) ?? "";
  const target = getForgeProxyTarget(host, url);
  return target ? target + url.slice(prefix.length) : url;
}

export function getImageURL(url: string) {
  if (url.startsWith("/global_icons/game-icons")) {
    return `${DATA_FORGE_CDN_URL}${url.replace("/global_icons", "")}`;
  }
  return url;
}

export function getAppUrl(appName: string, path: string): string {
  return `${DATA_FORGE_CDN_URL}/${appName}${path}`;
}

/**
 * Cache-bust lever for map tiles. Tile URLs are content-hashed and served with
 * `Cache-Control: immutable`, so a poisoned browser entry (e.g. a 404 cached
 * during a tile outage) never revalidates and sticks for up to a year. Bumping
 * this appends a new `?v=` to every tile request, so clients fetch a fresh URL
 * and bypass the stale entry WITHOUT re-tiling — the CDN serves the same files
 * under the new query. Only the visible tiles re-download per session.
 *
 * Bumping busts tiles for ALL games/users once. Set to 0 to disable the param.
 * (2026-06-22: introduced at v=1 to recover clients poisoned by the Crimson
 * Desert tile outage.)
 */
export const TILE_CACHE_VERSION = 1;

/** Full CDN tile-layer URL with the cache-bust version appended. */
export function getTileLayerUrl(appName: string, tilePath: string): string {
  const url = getAppUrl(appName, tilePath);
  if (!TILE_CACHE_VERSION) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${TILE_CACHE_VERSION}`;
}

export function getApiUrl(appName: string, searchParams: string): string {
  return `${DATA_FORGE_URL}/api/${appName}/search?${searchParams}`;
}

export function getPreviewImageUrl(
  appName: string,
  mapName: string,
  version?: string,
): string {
  const url = `${DATA_FORGE_CDN_URL}/${appName}/map-tiles/${mapName}/preview.webp`;
  return version ? `${url}?v=${version}` : url;
}

export function getOpenGraphImageUrl(appName: string, mapName: string): string {
  return `${DATA_FORGE_CDN_URL}/${appName}/map-tiles/${mapName}/opengraph-image.jpg`;
}

/**
 * Fetch JSON and cache it in a module-level Map keyed by URL. Bypasses
 * Next.js's data cache (which has a hard 2 MB ceiling on the parsed JS
 * representation) and runs our own per-process LRU with a TTL.
 *
 * Use for endpoints whose parsed response can exceed 2 MB (version.json on
 * games with lots of regions, dicts/<locale>-desc.json on text-heavy games)
 * or whose payload doesn't fit Next.js's tag/path invalidation model.
 *
 * Stale-while-revalidate: once a URL is cached, callers never wait on the
 * network for it again. An expired entry is returned immediately and refreshed
 * in the background; a failed refresh keeps serving the old copy. Before this,
 * every expiry made an SSR render await cdn.th.gl, and a hung connect (undici
 * ETIMEDOUT after 10 s) held the render and 5xx'd the page. During the
 * 2026-09-25 Googlebot crawl that spilled into 499s on every tenant.
 *
 * `immutable` entries (URL pinned to a content hash, see withContentHash) are
 * never refreshed. Total cached bytes are capped; least recently used entries
 * are evicted first. Dedupes concurrent requests for the same URL so cold
 * renders don't double-fetch.
 */
type MemoryFetchEntry = {
  data: unknown;
  expiresAt: number;
  bytes: number;
  /** cdn.th.gl ETag, sent as If-None-Match on the refresh. */
  etag?: string;
};
// Next bundles this module into several server layers (RSC, SSR, route
// handlers, instrumentation: 4-5 copies per pod, [pod-health] jsonCopies
// 2026-10-06). Module-level Maps gave each copy its own cache, so every pod
// fetched, parsed and held each 10 MB dict up to 5 times. The state lives on
// globalThis so all copies share one cache and one byte budget.
type MemoryFetchState = {
  cache: Map<string, MemoryFetchEntry>;
  inflight: Map<string, Promise<unknown>>;
  bytes: number;
  copies: number;
  /** Per-minute churn counters, read + reset by memoryFetchCacheStats. */
  churn: MemoryFetchChurn;
  /** URLs evicted for the byte budget (insertion order = eviction order). */
  evicted: Set<string>;
};
type ChurnCount = { n: number; bytes: number };
type MemoryFetchChurn = {
  /** Full 200 downloads + parses. */
  loads: ChurnCount;
  /** Conditional refreshes answered 304 (parsed copy kept). */
  notModified: number;
  /** Entries dropped for the byte budget. */
  evictions: ChurnCount;
  /** Full loads of a URL this cache had evicted before. */
  refetches: ChurnCount;
  evictedBy: Map<string, ChurnCount>;
  refetchedBy: Map<string, ChurnCount>;
};
const newChurn = (): MemoryFetchChurn => ({
  loads: { n: 0, bytes: 0 },
  notModified: 0,
  evictions: { n: 0, bytes: 0 },
  refetches: { n: 0, bytes: 0 },
  evictedBy: new Map(),
  refetchedBy: new Map(),
});
const memoryFetch = ((
  globalThis as { __thglMemoryFetch?: MemoryFetchState }
).__thglMemoryFetch ??= {
  cache: new Map(),
  inflight: new Map(),
  bytes: 0,
  copies: 0,
  churn: newChurn(),
  evicted: new Set(),
});
// A state created by an older copy of this module (dev HMR) lacks these.
memoryFetch.churn ??= newChurn();
memoryFetch.evicted ??= new Set();
memoryFetch.copies++;
const memoryFetchCache = memoryFetch.cache;
const memoryFetchInflight = memoryFetch.inflight;
const MEMORY_FETCH_TTL_MS = 60_000;
// Budget in estimated HEAP bytes, not JSON text: a parsed dict/database object
// takes 3-4.5x its text length (string headers, object shapes, hash tables;
// measured on Albion/BG/WWM/Infinity Nikki dicts with node --expose-gc).
// At 1 GB every pod sat at the budget and IL/NY re-downloaded + re-parsed
// ~225 MB/min of evicted dicts (98% of their JSON loads, data-forge inbox
// #1558). 3 GB fits the busy pods' working set; the Dockerfile raises the V8
// heap limit to 8 GB to match (container has 32 GB). Dicts (`compact`) count
// their text + index instead (~2 bytes/char): flat strings that the GC marks
// in O(1), where 3 GB of parsed dicts doubled the max GC pause (#1599).
const MEMORY_FETCH_MAX_BYTES = 3 * 1024 * 1024 * 1024;
const PARSED_BYTES_PER_CHAR = 4;

// Remembered evicted URLs, so a later full load can be told apart as churn.
const EVICTED_URLS_KEPT = 50_000;

const LOCALE_PART = /^[a-z]{2}(-[A-Za-z]{2,4})?$/;

/**
 * Cache-key class for the churn counters: `<game>/<path>` without the content
 * hash, with locales and per-entry ids collapsed (`palia/dicts/*.json`,
 * `palia/dicts/db/desc/*\/*.json`, `palia/config/database.items/*.json`).
 */
export function memoryFetchUrlClass(url: string): string {
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    path = url.split("?")[0];
  }
  const seg = path.split("/").filter(Boolean);
  for (let i = 1; i < seg.length; i++) {
    const last = i === seg.length - 1;
    const name = last ? seg[i].replace(/\.json$/, "") : seg[i];
    const parent = seg[i - 1];
    if (
      (LOCALE_PART.test(name) && name !== "db") ||
      (last &&
        (parent === "*" ||
          parent === "dialogue" ||
          parent.startsWith("database.")))
    ) {
      seg[i] = last ? "*.json" : "*";
    }
  }
  return seg.join("/");
}

const bumpChurn = (m: Map<string, ChurnCount>, key: string, bytes: number) => {
  const c = m.get(key);
  if (c) {
    c.n++;
    c.bytes += bytes;
  } else m.set(key, { n: 1, bytes });
};

/**
 * Shared JSON memory cache size + this interval's churn, for the [pod-health]
 * log line. Resets the churn counters (one caller, once a minute).
 */
export function memoryFetchCacheStats(topN = 8) {
  const churn = memoryFetch.churn;
  memoryFetch.churn = newChurn();
  const mb = (bytes: number) => Math.round(bytes / 1048576);
  const top = (m: Map<string, ChurnCount>) =>
    Object.fromEntries(
      [...m]
        .sort((a, b) => b[1].bytes - a[1].bytes)
        .slice(0, topN)
        .map(([k, c]) => [k, { n: c.n, mb: mb(c.bytes) }]),
    );
  // What the budget is spent on right now, by URL class.
  const held = new Map<string, ChurnCount>();
  for (const [url, entry] of memoryFetchCache) {
    const key = memoryFetchUrlClass(url).replace(/^[^/]+\//, "*/");
    const c = held.get(key);
    if (c) {
      c.n++;
      c.bytes += entry.bytes;
    } else held.set(key, { n: 1, bytes: entry.bytes });
  }
  return {
    copies: memoryFetch.copies,
    entries: memoryFetchCache.size,
    mb: mb(memoryFetch.bytes),
    loads: { n: churn.loads.n, mb: mb(churn.loads.bytes) },
    notModified: churn.notModified,
    evictions: { n: churn.evictions.n, mb: mb(churn.evictions.bytes) },
    refetches: { n: churn.refetches.n, mb: mb(churn.refetches.bytes) },
    evictedBy: top(churn.evictedBy),
    refetchedBy: top(churn.refetchedBy),
    heldBy: top(held),
  };
}

type MemoryFetchOptions<T> = {
  onNotFound?: () => T | undefined;
  ttlMs?: number;
  immutable?: boolean;
  /**
   * How long past its TTL an entry may still be served while it refreshes in
   * the background. Beyond that the call waits for the fresh copy (the stale
   * one only on failure). Unset = serve stale forever, refresh behind it.
   */
  maxStaleMs?: number;
  /** Called whenever fresh data is stored — the first load and every background refresh. */
  onFresh?: (data: T) => void;
  /**
   * Flat JSON object (a dict): on the server, keep it as its text behind a
   * read-only-ish Proxy instead of a parsed object (compact-dict.ts), so the
   * cached dicts stop costing old-generation GC time.
   */
  compact?: boolean;
};

function storeMemoryFetchEntry(url: string, entry: MemoryFetchEntry) {
  const previous = memoryFetchCache.get(url);
  if (previous) {
    memoryFetch.bytes -= previous.bytes;
    memoryFetchCache.delete(url);
  }
  memoryFetchCache.set(url, entry);
  memoryFetch.bytes += entry.bytes;
  // Map iteration is insertion order and hits re-insert, so this walks LRU-first.
  for (const [key, oldest] of memoryFetchCache) {
    if (memoryFetch.bytes <= MEMORY_FETCH_MAX_BYTES || key === url) break;
    memoryFetchCache.delete(key);
    memoryFetch.bytes -= oldest.bytes;
    memoryFetch.churn.evictions.n++;
    memoryFetch.churn.evictions.bytes += oldest.bytes;
    bumpChurn(
      memoryFetch.churn.evictedBy,
      memoryFetchUrlClass(key),
      oldest.bytes,
    );
    const evicted = memoryFetch.evicted;
    evicted.delete(key);
    evicted.add(key);
    if (evicted.size > EVICTED_URLS_KEPT) {
      evicted.delete(evicted.values().next().value as string);
    }
  }
}

/** One retry for network failures (DNS, connect timeout), not HTTP errors. */
async function fetchNoStore(url: string, etag?: string): Promise<Response> {
  const init: RequestInit = {
    cache: "no-store",
    headers: etag ? { "If-None-Match": etag } : undefined,
  };
  try {
    return await fetch(url, init);
  } catch {
    return fetch(url, init);
  }
}

function loadIntoMemoryCache<T>(
  url: string,
  options?: MemoryFetchOptions<T>,
): Promise<T> {
  const inflight = memoryFetchInflight.get(url);
  if (inflight) return inflight as Promise<T>;

  const ttl = options?.ttlMs ?? MEMORY_FETCH_TTL_MS;
  const expiresAt = () =>
    options?.immutable ? Number.POSITIVE_INFINITY : Date.now() + ttl;
  const promise = (async () => {
    // Refreshes are conditional: an unchanged file answers 304 with no body,
    // so the cached copy is kept instead of downloaded and parsed again. Without
    // this every busy pod re-fetched and re-parsed every version.json (up to
    // 2.9 MB each, 20 s TTL) around the clock; the GC load starved the IL/NY
    // pods into a liveness restart loop on 2026-10-13 (data-forge inbox #1500).
    const previous = memoryFetchCache.get(url);
    const res = await fetchNoStore(url, previous?.etag);
    if (res.status === 304 && previous) {
      memoryFetch.churn.notModified++;
      storeMemoryFetchEntry(url, { ...previous, expiresAt: expiresAt() });
      return previous.data as T;
    }
    if (!res.ok) {
      if (res.status === 404 && options?.onNotFound) {
        const fallback = options.onNotFound();
        if (fallback !== undefined) {
          // Cache the fallback like a normal result — otherwise every call
          // for a missing resource re-fetches the 404 from the CDN.
          storeMemoryFetchEntry(url, {
            data: fallback,
            expiresAt: expiresAt(),
            bytes: 0,
          });
          return fallback;
        }
      }
      throw new Error(`Failed to fetch ${url}: ${res.status}`);
    }
    const text = await res.text();
    const parsed =
      options?.compact && typeof window === "undefined"
        ? parseCompactJson<T>(text)
        : { data: JSON.parse(text) as T, bytes: null };
    const data = parsed.data;
    const bytes = parsed.bytes ?? text.length * PARSED_BYTES_PER_CHAR;
    const churn = memoryFetch.churn;
    churn.loads.n++;
    churn.loads.bytes += bytes;
    if (!previous && memoryFetch.evicted.delete(url)) {
      churn.refetches.n++;
      churn.refetches.bytes += bytes;
      bumpChurn(churn.refetchedBy, memoryFetchUrlClass(url), bytes);
    }
    storeMemoryFetchEntry(url, {
      data,
      expiresAt: expiresAt(),
      bytes,
      etag: res.headers.get("etag") ?? undefined,
    });
    options?.onFresh?.(data);
    return data;
  })().finally(() => {
    memoryFetchInflight.delete(url);
  });
  memoryFetchInflight.set(url, promise);
  return promise;
}

export async function fetchJsonWithMemoryCache<T>(
  url: string,
  options?: MemoryFetchOptions<T>,
): Promise<T> {
  // Resolve before the cache lookup so the cache is keyed by the real
  // target (local vs prod forge differ per request in dev proxy mode).
  url = await resolveForgeUrl(url);
  const ttl = options?.ttlMs ?? MEMORY_FETCH_TTL_MS;
  const cached = memoryFetchCache.get(url);
  // ttl 0 (dev version.json) opts out of serving stale.
  if (cached && (ttl > 0 || cached.expiresAt > Date.now())) {
    memoryFetchCache.delete(url);
    memoryFetchCache.set(url, cached);
    const maxStale = options?.maxStaleMs;
    if (
      maxStale !== undefined &&
      ttl > 0 &&
      cached.expiresAt + maxStale <= Date.now()
    ) {
      return loadIntoMemoryCache(url, options).catch((error) => {
        cached.expiresAt = Date.now() + ttl;
        console.warn(`Refresh failed, serving stale ${url}:`, error);
        return cached.data as T;
      });
    }
    if (cached.expiresAt <= Date.now() && !memoryFetchInflight.has(url)) {
      loadIntoMemoryCache(url, options).catch((error) => {
        // Keep serving the stale copy; retry after another TTL.
        cached.expiresAt = Date.now() + ttl;
        console.warn(`Background refresh failed, serving stale ${url}:`, error);
      });
    }
    return cached.data as T;
  }
  return loadIntoMemoryCache(url, options);
}

/**
 * Called with every version.json a render actually uses. games-web's server
 * registers one so its page cache can stamp each cached page with the data
 * version it was rendered from and drop it once the game's data changes.
 */
type VersionObserver = (appName: string, versionId: string) => void;
// On globalThis (not module state) for the same reason as the request-host
// resolver below: the registering module may not share module instances with
// the RSC server graph that calls fetchVersion.
const VERSION_OBSERVER_KEY = "__thglVersionObserver";
export function setVersionObserver(fn: VersionObserver | null) {
  (globalThis as Record<string, unknown>)[VERSION_OBSERVER_KEY] = fn;
}

export async function fetchVersion(appName: string): Promise<Version> {
  const observe = (v: Version | undefined) => {
    const observer = (globalThis as Record<string, unknown>)[
      VERSION_OBSERVER_KEY
    ] as VersionObserver | null | undefined;
    if (observer && v?.id) observer(appName, v.id);
  };
  const version = await fetchJsonWithMemoryCache<Version>(
    getAppUrl(appName, "/version.json"),
    {
      // Shorter than other files: a small file, and a data update must reach
      // every pod quickly (data-forge inbox #1315).
      ttlMs: process.env.NODE_ENV === "development" ? 0 : 20_000,
      // Bounded: every pod renders the new data version at most 30 s after a
      // data update, even one that sat idle for hours - games-web's
      // /api/revalidate re-purges the tenant's edge after 75 s and relies on
      // it (an idle pod's first render must not refill the edge with old data).
      maxStaleMs: 10_000,
      // A background refresh (stale-while-revalidate) reports the new version
      // as soon as it lands, not only on the next call.
      onFresh: observe,
    },
  );
  observe(version);
  return version;
}

/**
 * Pin a data-forge file URL to the game's content hash (version.json
 * `more.contentHash`, which data-forge changes whenever the dicts or database
 * output change). Hashed URLs are cached until the next data update instead of
 * being re-fetched every minute; the `?v=` also keys the CDN edge cache.
 * version.json files that predate the field keep the TTL behaviour.
 */
async function withContentHash(
  appName: string,
  url: string,
): Promise<{ url: string; immutable: boolean }> {
  const hash = await fetchVersion(appName)
    .then((version) => version.more.contentHash)
    .catch(() => undefined);
  if (!hash) return { url, immutable: false };
  return {
    url: `${url}${url.includes("?") ? "&" : "?"}v=${hash}`,
    immutable: true,
  };
}

// Cache for version lookup maps to avoid recreating them on each call
const versionCacheMap = new WeakMap<
  Version,
  {
    tileKeys: Set<string>;
    filterValueIds: Set<string>;
    filterGroupIds: Set<string>;
  }
>();

// Reverse lookups per dict, per candidate key set (keyed by object reference)
const dictCacheMap = new WeakMap<
  Record<string, string>,
  WeakMap<Set<string>, Map<string, string[]>>
>();

function getVersionLookupCache(version: Version) {
  let cache = versionCacheMap.get(version);

  if (!cache) {
    // Build set of valid tile keys
    const tileKeys = new Set(Object.keys(version.data.tiles));

    // Build set of valid filter value IDs
    const filterValueIds = new Set<string>();
    const filterGroupIds = new Set<string>();
    for (const filter of version.data.filters) {
      filterGroupIds.add(filter.group);
      for (const value of filter.values) {
        filterValueIds.add(value.id);
      }
    }

    cache = { tileKeys, filterValueIds, filterGroupIds };
    versionCacheMap.set(version, cache);
  }

  return cache;
}

/**
 * Display name -> the `candidates` keys (tile keys, filter ids, group ids) whose
 * pointer-resolved dict value it is, in dict order. Only the candidates are
 * indexed: a whole-dict reverse map was another copy of every dict in the heap
 * (data-forge inbox #1599), and every caller filters to one candidate set.
 */
function getReverseDictMap(
  dict: Record<string, string>,
  candidates: Set<string>,
): Map<string, string[]> {
  let byCandidates = dictCacheMap.get(dict);
  if (!byCandidates) dictCacheMap.set(dict, (byCandidates = new WeakMap()));
  let reverseDictMap = byCandidates.get(candidates);

  if (!reverseDictMap) {
    // Resolve pointer values (e.g. "@other_key" -> dict["@other_key"])
    reverseDictMap = new Map<string, string[]>();
    for (const key of sortByDictOrder(dict, [...candidates])) {
      const value = dict[key];
      if (value === undefined) continue;
      const resolved =
        value && value[0] === "@" ? (dict[value] ?? value) : value;
      const existing = reverseDictMap.get(resolved);
      if (existing) existing.push(key);
      else reverseDictMap.set(resolved, [key]);
    }
    byCandidates.set(candidates, reverseDictMap);
  }

  return reverseDictMap;
}

export function getMapNameFromVersion(
  version: Version,
  map: string,
  dict: Record<string, string>,
): string | null {
  const decodedMap = decodeURIComponent(map);
  const { tileKeys } = getVersionLookupCache(version);
  const reverseDictMap = getReverseDictMap(dict, tileKeys);

  const possibleKeys = reverseDictMap.get(decodedMap);
  if (!possibleKeys) return null;
  // Find first key that exists in tiles
  for (const key of possibleKeys) {
    if (key[0] === "@") {
      const resolvedKeys = reverseDictMap.get(key);
      if (!resolvedKeys) continue;

      for (const resolvedKey of resolvedKeys) {
        if (tileKeys.has(resolvedKey)) {
          return resolvedKey;
        }
      }
    } else if (tileKeys.has(key)) {
      return key;
    }
  }

  return null;
}

/**
 * Resolve a map URL segment to BOTH its tile key and its canonical display
 * name, tolerating '+'-as-space and wrong CASE. Used to 301/308-canonicalize
 * non-canonical map URLs (e.g. `/maps/palpagos%20island` or a legacy
 * `+`-encoded form) to the proper-cased `%20` URL that the sitemap emits.
 *
 * `name` is the canonical display name for the resolved tile in THIS dict
 * (locale) — encode it with encodeURIComponent to build the canonical path.
 * Returns null when the segment doesn't resolve to a real map (→ caller 404s).
 *
 * `fallbackDict` (the English dict, on non-en locales) also resolves a map
 * linked by its ENGLISH name - `/de/maps/Zero%20Dam` when the German term is
 * "Zero-Damm" - so English-named links (cards, tools, old bookmarks) 308 to
 * the localized canonical name instead of 404ing.
 */
export function getCanonicalMapName(
  version: Version,
  map: string,
  dict: Record<string, string>,
  fallbackDict?: Record<string, string>,
): { key: string; name: string } | null {
  // Mirror getMapNameFromVersion's decode, but also treat '+' as a space so a
  // legacy `/maps/Foo+Bar` still resolves (proxy normally 301s it first).
  const decodedMap = decodeURIComponent(map.replace(/\+/g, " "));
  const { tileKeys } = getVersionLookupCache(version);
  const reverseDictMap = getReverseDictMap(dict, tileKeys);

  // Given a display name, resolve to the first tile key it maps to (handles
  // pointer values, identical to getMapNameFromVersion).
  const resolveTileKey = (
    displayName: string,
    reverse: Map<string, string[]> = reverseDictMap,
  ): string | null => {
    const possibleKeys = reverse.get(displayName);
    if (!possibleKeys) return null;
    for (const key of possibleKeys) {
      if (key[0] === "@") {
        const resolvedKeys = reverse.get(key);
        if (!resolvedKeys) continue;
        for (const resolvedKey of resolvedKeys) {
          if (tileKeys.has(resolvedKey)) return resolvedKey;
        }
      } else if (tileKeys.has(key)) {
        return key;
      }
    }
    return null;
  };

  // 1) Exact match (the common, already-canonical case).
  let key = resolveTileKey(decodedMap);
  // 2) Case-insensitive fallback for wrong-cased URLs (crawlers/old links).
  if (!key) {
    const lower = decodedMap.toLowerCase();
    for (const [displayName] of reverseDictMap) {
      if (displayName.toLowerCase() === lower) {
        const resolved = resolveTileKey(displayName);
        if (resolved) {
          key = resolved;
          break;
        }
      }
    }
  }
  // 3) English/default fallback: match the tile key itself or its English
  // defaultTitle. Non-en dicts carry properly localized map names now, so
  // English-named URLs (home cards, hreflang alternates, old bookmarks) no
  // longer reverse-resolve through the locale dict — resolve them here and let
  // the caller 308 to the localized canonical name.
  if (!key) {
    const lower = decodedMap.toLowerCase();
    for (const [tileKey, tile] of Object.entries(version.data.tiles)) {
      if (
        tileKey.toLowerCase() === lower ||
        tile.defaultTitle?.toLowerCase() === lower
      ) {
        key = tileKey;
        break;
      }
    }
  }
  // 4) English dict fallback (exact, then case-insensitive): maps whose English
  // name only lives in dicts-en (no tile defaultTitle).
  if (!key && fallbackDict && fallbackDict !== dict) {
    const fallbackReverse = getReverseDictMap(fallbackDict, tileKeys);
    key = resolveTileKey(decodedMap, fallbackReverse);
    if (!key) {
      const lower = decodedMap.toLowerCase();
      for (const [displayName] of fallbackReverse) {
        if (displayName.toLowerCase() === lower) {
          key = resolveTileKey(displayName, fallbackReverse);
          if (key) break;
        }
      }
    }
  }
  if (!key) return null;

  // Canonical display name = the tile key's own dict term (pointer-resolved),
  // falling back to the key when it has no term.
  const raw = dict[key];
  const name = raw && raw[0] === "@" ? (dict[raw] ?? key) : (raw ?? key);
  return { key, name };
}

export function getTypeFromVersion(
  version: Version,
  type: string,
  dict: Record<string, string>,
): string | null {
  const decodedType = decodeURIComponent(type);
  const { filterValueIds } = getVersionLookupCache(version);
  const reverseDictMap = getReverseDictMap(dict, filterValueIds);

  const possibleKeys = reverseDictMap.get(decodedType);
  if (!possibleKeys) return null;

  // Find first key that exists in filter values
  for (const key of possibleKeys) {
    if (filterValueIds.has(key)) {
      return key;
    }
  }

  return null;
}

/** Return ALL filter value IDs that translate to the same English name */
export function getAllTypesFromVersion(
  version: Version,
  type: string,
  dict: Record<string, string>,
): string[] {
  const decodedType = decodeURIComponent(type);
  const { filterValueIds } = getVersionLookupCache(version);
  const reverseDictMap = getReverseDictMap(dict, filterValueIds);

  const possibleKeys = reverseDictMap.get(decodedType);
  if (!possibleKeys) return [];

  return possibleKeys.filter((key) => filterValueIds.has(key));
}

export function getGroupFromVersion(
  version: Version,
  group: string,
  dict: Record<string, string>,
): string | null {
  const decodedGroup = decodeURIComponent(group);
  const { filterGroupIds } = getVersionLookupCache(version);
  const reverseDictMap = getReverseDictMap(dict, filterGroupIds);

  const possibleKeys = reverseDictMap.get(decodedGroup);
  if (!possibleKeys) return null;

  // Find first key that exists in filter groups
  for (const key of possibleKeys) {
    if (filterGroupIds.has(key)) {
      return key;
    }
  }

  return null;
}

export function getIconsUrl(
  appName: string,
  icon: string,
  iconPath?: string,
): string {
  if (icon.startsWith("/global_icons/game-icons")) {
    return `${DATA_FORGE_CDN_URL}${icon.replace("/global_icons", "")}`;
  }
  if (icon.includes("global_icons")) {
    return icon;
  }
  if ((icon === "icons.webp" || icon === "/icons/icons.webp") && iconPath) {
    return getAppUrl(appName, iconPath);
  }
  if (icon.startsWith("/")) {
    return getAppUrl(appName, icon);
  }
  return getAppUrl(appName, `/icons/${icon}`);
}

/**
 * Fetch a game's localisation dictionary. Routed through the module-level
 * memory cache (see `fetchJsonWithMemoryCache`) to sidestep Next.js's 2 MB
 * data-cache ceiling - some games' combined dicts (Avowed, Infinity Nikki,
 * Crimson Desert) parse to > 2 MB in V8 even though the source JSON is smaller.
 */
export async function fetchDict(
  appName: string,
  locale: string = "en",
): Promise<Record<string, string>> {
  const { url, immutable } = await withContentHash(
    appName,
    `${DATA_FORGE_CDN_URL}/${appName}/dicts/${locale}.json`,
  );
  const dict = await fetchJsonWithMemoryCache<Record<string, string> | null>(
    url,
    { onNotFound: () => null, immutable, compact: true },
  );
  if (dict !== null) return dict;
  // A locale without a dict on the CDN (tenant advertises more locales than the
  // data pipeline emits, or a crawler-cased URL like /zh-cn/) must not 500 the
  // render — fall back to English, matching getAppDictionary's behavior.
  if (locale === "en") {
    throw new Error(`Missing en dict for ${appName}`);
  }
  return fetchDict(appName, "en");
}

/** A game's split-out codex terms (`dicts/db/<locale>.json`), or null if it has none. */
async function fetchDbTerms(
  appName: string,
  locale: string,
): Promise<Record<string, string> | null> {
  const { url, immutable } = await withContentHash(
    appName,
    `${DATA_FORGE_CDN_URL}/${appName}/dicts/db/${locale}.json`,
  );
  const terms = await fetchJsonWithMemoryCache<Record<string, string> | null>(
    url,
    { onNotFound: () => null, immutable, compact: true },
  );
  if (terms !== null || locale === "en") return terms;
  return fetchDbTerms(appName, "en");
}

// Keyed weakly on the cached inputs: a merge lives exactly as long as both
// inputs are still in the memory fetch cache. A strong per-game/locale Map kept
// every evicted dict + terms + merge alive, bypassing the cache's byte budget
// (Infinity Nikki alone: ~10 MB codex dict per locale x 12 locales). A merge of
// compact dicts is a layered view, not a copy (layerDicts).
const dbDictMerges = new WeakMap<
  Record<string, string>,
  WeakMap<Record<string, string>, Record<string, string>>
>();

/**
 * The dictionary for /db pages: the game's dict merged with its split-out codex terms.
 * data-forge `write({ splitDbDict })` moves codex-only names/descriptions to
 * `dicts/db/<locale>.json` because the interactive map ships its whole dict to the
 * client. Games that don't split have no such file, so this equals `fetchDict`.
 * Pointer keys never collide across the two files (`@…` vs `@d…`).
 */
export async function fetchDbDict(
  appName: string,
  locale: string = "en",
): Promise<Record<string, string>> {
  const [dict, terms] = await Promise.all([
    fetchDict(appName, locale),
    fetchDbTerms(appName, locale),
  ]);
  if (!terms) return dict;
  // Both inputs come from the memory cache, so reuse the merge while they're unchanged.
  let byTerms = dbDictMerges.get(dict);
  if (!byTerms) dbDictMerges.set(dict, (byTerms = new WeakMap()));
  let merged = byTerms.get(terms);
  if (!merged) byTerms.set(terms, (merged = layerDicts(dict, terms)));
  return merged;
}

/**
 * Description shards per locale (`dicts/db/desc/<locale>/<bucket>.json`). MUST
 * match `DB_DESC_BUCKETS` in data-forge `data-mining/src/lib/dicts.ts`.
 */
export const DB_DESC_BUCKETS = 64;

/** Bucket file name of a `<id>_desc` key. Mirror of data-forge's dbDescBucket. */
export function dbDescBucket(key: string): string {
  let h = 5381;
  for (let i = 0; i < key.length; i++) {
    h = (Math.imul(h, 33) ^ key.charCodeAt(i)) >>> 0;
  }
  return (h % DB_DESC_BUCKETS).toString(16).padStart(2, "0");
}

/** A locale's split-out codex file, falling back to English; null = none published. */
async function fetchDbSplitFile(
  appName: string,
  locale: string,
  path: (locale: string) => string,
): Promise<Record<string, string> | null> {
  for (const loc of locale === "en" ? ["en"] : [locale, "en"]) {
    const { url, immutable } = await withContentHash(
      appName,
      `${DATA_FORGE_CDN_URL}/${appName}/dicts/db/${path(loc)}`,
    );
    const file = await fetchJsonWithMemoryCache<Record<string, string> | null>(
      url,
      { onNotFound: () => null, immutable, compact: true },
    );
    if (file) return file;
  }
  return null;
}

/**
 * fetchDbDict WITHOUT the codex descriptions — for the /db hot path (layout +
 * detail pages). data-forge writes `dicts/db/names/<locale>.json` next to the
 * full codex dict; descriptions are ~90% of it (Infinity Nikki th: 10.4 MB full
 * vs 0.95 MB names). Read a description with fetchDbDescription. Games without
 * a names file (no split, or not regenerated yet) get the full fetchDbDict.
 */
export async function fetchDbNamesDict(
  appName: string,
  locale: string = "en",
): Promise<Record<string, string>> {
  const [dict, names] = await Promise.all([
    fetchDict(appName, locale),
    fetchDbSplitFile(appName, locale, (l) => `names/${l}.json`),
  ]);
  if (!names) return fetchDbDict(appName, locale);
  let byNames = dbDictMerges.get(dict);
  if (!byNames) dbDictMerges.set(dict, (byNames = new WeakMap()));
  let merged = byNames.get(names);
  if (!merged) byNames.set(names, (merged = layerDicts(dict, names)));
  return merged;
}

/**
 * One codex description (`<id>_desc`) from its shard, for pages built on
 * fetchDbNamesDict. Undefined when the game has no shards or no such text.
 */
export async function fetchDbDescription(
  appName: string,
  locale: string,
  id: string,
): Promise<string | undefined> {
  const key = `${id}_desc`;
  const shard = await fetchDbSplitFile(
    appName,
    locale,
    (l) => `desc/${l}/${dbDescBucket(key)}.json`,
  );
  return shard?.[key];
}

/** One line a character says, with the player's reply options (data-forge lib/dialogue.ts). */
export type DialogueLine = { t: string; r?: string[] };

/**
 * A codex entry's dialogue (`config/dialogue/<locale>/<id>.json`), for entries
 * with `props._dialogue`. Kept out of the dicts and database files (megabytes
 * per locale). Undefined when the entry has none.
 */
export async function fetchDialogue(
  appName: string,
  locale: string,
  id: string,
): Promise<DialogueLine[] | undefined> {
  for (const loc of locale === "en" ? ["en"] : [locale, "en"]) {
    const file = await fetchDatabaseFile<{ lines: DialogueLine[] } | null>(
      appName,
      `dialogue/${loc}/${encodeURIComponent(id)}.json`,
      () => null,
    );
    if (file) return file.lines;
  }
  return undefined;
}

/** Entry id → line count of every entry with dialogue (`config/dialogue/index.json`). */
export async function fetchDialogueIndex(
  appName: string,
): Promise<Record<string, number>> {
  return fetchDatabaseFile<Record<string, number>>(
    appName,
    "dialogue/index.json",
    () => ({}),
  );
}

/**
 * Every entry's lines in one locale (`config/dialogue/<locale>.json`, entry id →
 * lines, no replies) for the client-side Quotes search. Megabytes: fetched in
 * the browser only, never during a render.
 */
export function dialogueCorpusUrl(
  appName: string,
  locale: string,
  contentHash?: string,
): string {
  return `${DATA_FORGE_CDN_URL}/${appName}/config/dialogue/${locale}.json${contentHash ? `?v=${contentHash}` : ""}`;
}

/**
 * Database files go through the memory cache pinned to the content hash, so a
 * crawl across thousands of /db pages costs one CDN fetch per file per server
 * per data update instead of one every minute.
 */
async function fetchDatabaseFile<T>(
  appName: string,
  path: string,
  onNotFound?: () => T,
): Promise<T> {
  const { url, immutable } = await withContentHash(
    appName,
    `${DATA_FORGE_CDN_URL}/${appName}/config/${path}`,
  );
  return fetchJsonWithMemoryCache<T>(url, { onNotFound, immutable });
}

export async function fetchDatabase(appName: string): Promise<DatabaseConfig> {
  return fetchDatabaseFile<DatabaseConfig>(appName, "database.json");
}

/**
 * Fetch the slim per-type index when the app's database is split into multiple
 * files (e.g. when it would otherwise exceed Next.js's 2 MB fetch cache limit).
 * Each item carries `id`, `icon`, `groupId` and any small lite props the data
 * pipeline opted in to. Use this for sidebars, search indices and cross-link
 * lookups across all types.
 */
export async function fetchDatabaseIndex(
  appName: string,
): Promise<DatabaseConfig> {
  const index = await fetchDatabaseFile<DatabaseConfig | null>(
    appName,
    "database.index.json",
    () => null,
  );
  // Games that ship a single monolith database.json (no split index, e.g. BPSR)
  // fall back to it so the home/db section counts and listings still work.
  return index ? expandIndexIcons(index) : fetchDatabase(appName);
}

// The memory cache hands every caller the same parsed object; expand it once.
const expandedIndexes = new WeakSet<DatabaseConfig>();

/**
 * Undo data-forge's compact index icons (`compactIndexIcons` in its
 * lib/database.ts): a category with `dbIcon: {width, height}` omits `icon` on
 * every item whose icon is the standalone `db/<id>.webp` at 0,0, and marks items
 * WITHOUT an icon `icon: null`. It keeps the index (every item of every section
 * in one file) under the fetch-cache cap — Conan Exiles was at 1.48 MB with the
 * repeated sprite object over half of it. Expanded in place, so every reader
 * keeps seeing full icons.
 */
export function expandIndexIcons(index: DatabaseConfig): DatabaseConfig {
  if (expandedIndexes.has(index)) return index;
  for (const cat of index as (DatabaseConfig[number] & {
    dbIcon?: { width: number; height: number };
  })[]) {
    const dbIcon = cat.dbIcon;
    if (!dbIcon) continue;
    for (const item of cat.items) {
      if (item.icon === null) delete item.icon;
      else if (item.icon === undefined)
        item.icon = { url: `db/${item.id}.webp`, x: 0, y: 0, ...dbIcon };
    }
    delete cat.dbIcon;
  }
  expandedIndexes.add(index);
  return index;
}

/**
 * Fetch a single type's full database entry (one `{type, items}` category with
 * full `props` per item). Pair with `fetchDatabaseIndex` for cross-link data.
 */
export async function fetchDatabaseType(
  appName: string,
  type: string,
): Promise<DatabaseConfig[number]> {
  const category = await fetchDatabaseFile<DatabaseConfig[number] | null>(
    appName,
    `database.${type}.json`,
    () => null,
  );
  // Games shipping a single monolith database.json (no per-type split — Once
  // Human, Crimson Desert, Dune: Awakening and 11 others) have no such file, so
  // this 404s. A 404 body is not JSON, so `res.json()` threw and EVERY /db page
  // for those games returned 500 in production. Mirror fetchDatabaseIndex and
  // read the category out of the monolith instead.
  //
  // A type genuinely absent from the monolith yields an empty category, so the
  // caller's `items.find(...)` misses and the page 404s — the correct answer for
  // an entry that does not exist. We deliberately do NOT swallow a failing
  // monolith fetch: if neither file is reachable the data is broken, and an error
  // is more useful than silently serving an empty database.
  if (!category) {
    const db = await fetchDatabase(appName);
    return db.find((cat) => cat.type === type) ?? { type, items: [] };
  }
  return category;
}

/**
 * Fetch ONE database entry, for categories the index marks with `entries: true`.
 *
 * Why this exists: rendering a detail page used to `fetchDatabaseType` and then
 * `items.find(i => i.id === id)` — pulling and JSON-parsing up to 1.3 MB to use a
 * few hundred bytes. Measured in production that cost a cold render about +0.15s
 * on the biggest types, which multiplied straight into origin capacity during the
 * 2026-09-13 crawl. Per-entry files cut that to roughly 500 bytes.
 *
 * The body deliberately has NO `icon` (sprite coordinates move on every icon
 * repack, so keeping them out avoids a one-icon change rewriting tens of
 * thousands of files) — read the icon from `fetchDatabaseIndex` instead.
 *
 * Returns null when the entry is not found, so callers can fall back to the full
 * type file rather than 404 a page that genuinely exists.
 */
export async function fetchDatabaseEntry(
  appName: string,
  type: string,
  id: string,
): Promise<DatabaseConfig[number]["items"][number] | null> {
  return fetchDatabaseFile<DatabaseConfig[number]["items"][number] | null>(
    appName,
    `database.${type}/${encodeURIComponent(id)}.json`,
    () => null,
  ).catch(() => null);
}

/**
 * Old entry id → the entry it was merged into (`config/database.aliases.json`,
 * written by data-forge's lib/database.ts from each entry's `aliases`). The DB
 * entry page 308-redirects an unknown id found here, so URLs already in search
 * indexes survive a merge. Empty for games that never merged entries.
 */
export async function fetchDatabaseAliases(
  appName: string,
): Promise<Record<string, string>> {
  return fetchDatabaseFile<Record<string, string>>(
    appName,
    "database.aliases.json",
    () => ({}),
  ).catch(() => ({}));
}

export async function fetchTiles(appName: string): Promise<TilesConfig> {
  // Same per-process memory cache as the other data files. A Next data-cache
  // fetch (`next: { revalidate: 60 }`) here capped every cached page that
  // shows a map (codex entries) at a 60 s revalidate.
  return fetchJsonWithMemoryCache<TilesConfig>(
    `${DATA_FORGE_CDN_URL}/${appName}/config/tiles.json`,
  );
}

export type GlobalFiltersConfig = Array<{
  group: string;
  values: Array<{
    id: string;
    defaultOn?: boolean;
  }>;
}>;

export type DrawingsConfig = {
  name: string;
  isShared?: boolean;
  url?: string;
  nodes?: PrivateNode[];
  drawing?: Drawing;
}[];

export type IconSprite = {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type Icon = string | IconSprite;
export type DatabaseConfig<T = Record<string, any>> = {
  type: string;
  /**
   * Set by data-forge on categories that ALSO ship one file per entry at
   * `config/database.<type>/<id>.json` (types big enough that pulling the whole
   * type to render one detail page was expensive). When true, prefer
   * `fetchDatabaseEntry`; when absent, the full type file is the only source.
   * Always treat it as optional — older games keep shipping without it until
   * their next regeneration.
   */
  entries?: boolean;
  items: {
    id: string;
    icon?: Icon;
    props: T;
    groupId?: string;
  }[];
}[];

export type FiltersConfig = {
  group: string;
  category?: string;
  defaultOn?: boolean;
  values: {
    id: string;
    icon:
      | string
      | {
          name: string;
          url: string;
          x: number;
          y: number;
          width: number;
          height: number;
        };
    size?: number;
    sort?: number;
    /** No plotted map markers for this type (only shown via the in-app live overlay). */
    no_map_markers?: boolean;
    autoDiscover?: boolean;
    defaultOn?: boolean;
    // Stable identifier shared by all variants of the same underlying entity
    // (base / starred / infected / amber / masked / royal / magical / sized
    // siblings). Used by FilterSettingsPopover to offer a "Enable all
    // variants" toggle. Omitted for filters with no siblings.
    baseType?: string;
    // Former filter ids this type took over (renamed or split). A saved filter
    // selection that still holds one of them gets this type switched on
    // instead (`migrateReplacedFilters` in user.ts).
    replaces?: string[];
    // Existing filter id this type was split off from while that id stays
    // (Palia "<type>_amber" follows "<type>"). The first time a saved
    // selection meets this type it copies the followed filter's on/off state
    // instead of `defaultOn` (`migrateFilterChanges` in user.ts).
    follows?: string;
    // Codex/database section this marker type has an entry in. When set, the
    // marker panel/tooltip shows a "View in Codex" link to
    // `/db/<dbSection>/<spawn.dbEntryId ?? spawn.id ?? spawn.type>`: a spawn can
    // name its entry explicitly via `dbEntryId` (for position-derived spawn ids),
    // otherwise the DB entry is keyed by the spawn id for per-instance entries —
    // e.g. landmarks — or by the type id for per-type entries — e.g. a bestiary
    // species. Generic across all games.
    dbSection?: string;
    // Type-level codex entry id, when the entry for this type is NOT keyed by the type id
    // (e.g. filter `coal` → entry `item_coal`). Used after the spawn's own `dbEntryId`/id and
    // before the type-id fallback — the only way live actors of the type can link.
    dbEntryId?: string;
    // Never pair this type with a codex entry by English name: the shared name
    // is a different thing (data-forge `dbMapLink.exclude`).
    noDbLink?: boolean;
    // New/unproven filter we want community feedback on: an "Experimental"
    // badge on the filter row + a feedback hint in its tooltip.
    experimental?: boolean;
    // Area-of-effect radius in MAP units (same units as spawn positions, not
    // screen pixels). A circle of this radius is drawn around every live actor
    // of the type (e.g. Palia chum bucket's Star Quality fishing pool).
    rangeRadius?: number;
    // Hex colour (#RRGGBB) of the footprint outlines (`spawn.shape`) of this type's markers.
    shapeColor?: string;
    // "dots": every spawn of this type is drawn as a small `dotColor` dot and only a
    // spread-out few per zoom level show the full icon (more when zoomed in, never
    // overlapping) - for types with many spots (AION 2 monsters). Hovering a marker
    // enlarges every dot of the same look; selecting one fades the other dot types.
    display?: "dots";
    // Hex colour (#RRGGBB) of the dots of a `display: "dots"` type (default: per type id).
    dotColor?: string;
    // Live actors of this type carry their own names (`fetchLiveNames`, keyed by the raw
    // actor type): Palia "Plot Items" is one filter with a name per placed item.
    liveNames?: boolean;
    // Spots where the detected class is not this type (Once Human: the Securement Box model
    // as POI set dressing): live actors within `radius` (map units, 2D) of a `positions`
    // entry ([x, y] like spawn `p`, every map) are skipped (`buildLiveExcludeCheck`).
    liveExclude?: { radius: number; positions: [number, number][] };
    // Codex entries this ONE marker type stands for when the game can't tell them apart and
    // each is found somewhere else (Palia: Recipe: Fish Stew = Bahari ocean, Recipe: Sashimi
    // = Bahari rivers, one fishing blueprint). The markers mix their spots, so the type's
    // guide page shows each entry's own table instead of the map.
    mixedDbEntries?: { section: string; id: string }[];
  }[];
}[];

export type RegionsConfig = Region[];

export type TileLayer = {
  url?: string;
  defaultTitle?: string;
  options?: {
    minNativeZoom: number;
    maxNativeZoom: number;
    bounds: [[number, number], [number, number]];
    tileSize: number;
    threshold?: number;
  };
  minZoom?: number;
  maxZoom?: number;
  fitBounds?: [[number, number], [number, number]];
  view?: { center?: [number, number]; zoom?: number };
  transformation?: [number, number, number, number];
  threshold?: number;
  rotation?: {
    center: [number, number];
    angle: number;
  };
  /**
   * Marks this map as a FLOOR of a layered/hierarchical area (multi-floor
   * towers, underground, interiors). Floors of the same `group` are shown
   * together in the LayerSelect control and switched between like any map.
   * A layer map typically REUSES its parent's `url`/`options` (so the parent
   * tiles render as spatial context) and draws its own `overlay` on top.
   */
  layer?: {
    /** The map this is a floor of (whose tiles provide the backdrop). */
    parent: string;
    /** Area id grouping all floors together (e.g. the interior's name). */
    group: string;
    /** Sort order within the group; lower = higher floor (0 = surface). */
    floor: number;
    /** Display label for this floor (falls back to `defaultTitle`). */
    label?: string;
    /** Dict key of `label`: shown as t(labelTerm), `label` is the fallback. */
    labelTerm?: string;
    /**
     * Tight XY world footprint of the interior (shared by all floors of the
     * group). In live mode the apps auto-switch to this interior when the
     * player's position falls inside it. [[minLat,minLng],[maxLat,maxLng]].
     */
    footprint?: [[number, number], [number, number]];
    /**
     * This floor's height (Z) band [min,max] — used in live mode to pick which
     * floor of a multi-floor interior the player is on.
     */
    zRange?: [number, number];
  };
  /**
   * An interior image drawn over the tiles (the floor plan of a layered area),
   * positioned at `bounds` in map coordinates. Rendered via ImageOverlayLayer.
   */
  overlay?: {
    url: string;
    bounds: [[number, number], [number, number]];
    opacity?: number;
  };
  /**
   * Multiple interior overlays drawn at once — the single "Underground" map
   * renders every interior's floor plan, and the overworld draws them faint as
   * context. Each is placed at its own `bounds`.
   */
  overlays?: {
    url: string;
    bounds: [[number, number], [number, number]];
    /** Interior name — shown as an on-map "Underground" entrance button. */
    label?: string;
    /** Dict key of `label`: shown as t(labelTerm), `label` is the fallback. */
    labelTerm?: string;
  }[];
  /**
   * Per-interior footprints (tight XY world bounds). Live mode auto-switches to
   * the Underground when the player falls inside ANY of these.
   */
  footprints?: [[number, number], [number, number]][];
  /** Dim the underlying tiles so the `overlay`(s) read as the active floor. */
  backdrop?: boolean;
  /**
   * YYYY-MM-DD the map first appeared (stamped by data-forge's writeTiles).
   * Only list ORDER uses it (see sortMapNamesNewestFirst); maps that predate
   * the stamp have none.
   */
  addedAt?: string;
  /**
   * Selectable alternative layouts of this map (Dune Awakening's 12 Deep Desert
   * layouts, for private servers). `layout` is the official current one, whose
   * markers are the map's own node blob. See MapLayoutSelect.
   */
  layout?: number;
  layouts?: MapLayout[];
};
export type MapLayout = {
  id: number;
  /** Tile URL template of this layout (same bounds/transformation as the map). */
  url: string;
  /** Node blob path, used instead of `version.more.nodes[mapName]`. */
  nodes: string;
  /** Dict patch path with `{locale}`: names/descriptions of this layout's markers. */
  dicts: string;
  /** False when only the fixed markers are known (no resource sightings yet). */
  resources: boolean;
  /** The layout the single player mode always runs (Dune Deep Desert: layout 11). */
  singlePlayer?: true;
};
export type TilesConfig = Record<string, TileLayer>;

/**
 * Display name of a layer floor or an interior overlay in the current locale:
 * its `labelTerm` dict entry, else the English `label`.
 */
export function tileLabel(
  t: (term: string, options?: { fallback?: string }) => string,
  item: { label?: string; labelTerm?: string },
): string {
  const label = item.label ?? "";
  return item.labelTerm ? t(item.labelTerm, { fallback: label }) : label;
}

/**
 * Order map names for LISTINGS (home cards, /maps): maps with an `addedAt`
 * stamp first, newest first; unstamped maps after, in their config order.
 * Never use this for the default map — that stays the first tiles key.
 */
export function sortMapNamesNewestFirst(
  mapNames: string[],
  tiles: TilesConfig,
): string[] {
  const stamped = mapNames
    .filter((m) => tiles[m]?.addedAt)
    .sort((a, b) => tiles[b]!.addedAt!.localeCompare(tiles[a]!.addedAt!));
  const unstamped = mapNames.filter((m) => !tiles[m]?.addedAt);
  return [...stamped, ...unstamped];
}

/**
 * Whether two maps share the same world — the same map, a layer of the other
 * (e.g. an "Underground" whose `layer.parent` is the surface), or two layers of
 * the same parent. Layer maps reuse the parent's world transform, so a position
 * on one projects to the same spot on the other. `!!aParent` guards the sibling
 * check so two ordinary maps (both `parent === undefined`) are NOT treated as the
 * same world in games without layered maps.
 */
export function isSameWorld(
  a: string,
  b: string | undefined,
  tiles: TilesConfig,
): boolean {
  if (a === b) return true;
  if (!b) return false;
  const aParent = tiles[a]?.layer?.parent;
  const bParent = tiles[b]?.layer?.parent;
  return aParent === b || bParent === a || (!!aParent && aParent === bParent);
}

/**
 * Tebex (Alipay / WeChat Pay, China) on/off. Was OFF while the store waited
 * for Tebex's review (in Tebex Test Mode every checkout granted real perks for
 * free); ON since 2026-10-12 - Tebex approved, Leon published the store. Off hides the checkout (China page shows
 * "coming soon", /api/tebex/checkout refuses) and "Sign in with E-Mail"
 * (UI + /api/auth/email/request). The webhook, existing Tebex accounts and
 * the Account Key sign-in keep working. Flip to true after approval +
 * Test Mode off in the Tebex panel.
 */
export const TEBEX_ENABLED = true;
